import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { gsap, useGSAP } from '../../lib/gsap';
import { PRIMARY_NAV, SECONDARY_NAV } from './nav';

const isActivePath = (path: string, pathname: string) =>
   path === '/' ? pathname === '/' || pathname.startsWith('/event') : pathname.startsWith(path);

const Header = () => {
   const { pathname } = useLocation();
   const [open, setOpen] = useState(false);
   const [scrolled, setScrolled] = useState(false);
   const overlay = useRef<HTMLDivElement>(null);
   const tl = useRef<gsap.core.Timeline>();

   useEffect(() => setOpen(false), [pathname]);

   useEffect(() => {
      const onScroll = () => setScrolled(window.scrollY > 24);
      onScroll();
      window.addEventListener('scroll', onScroll, { passive: true });
      return () => window.removeEventListener('scroll', onScroll);
   }, []);

   useGSAP(
      () => {
         tl.current = gsap
            .timeline({ paused: true })
            .set(overlay.current, { display: 'flex' })
            .fromTo(
               overlay.current,
               { clipPath: 'circle(0% at calc(100% - 40px) 32px)' },
               { clipPath: 'circle(150% at calc(100% - 40px) 32px)', duration: 0.9, ease: 'expo.inOut' }
            )
            .from('[data-menu-item]', { yPercent: 110, stagger: 0.06, duration: 0.8 }, '-=0.35');
      },
      { scope: overlay }
   );

   useEffect(() => {
      if (!tl.current) return;
      open ? tl.current.timeScale(1).play() : tl.current.timeScale(1.8).reverse();
      document.body.style.overflow = open ? 'hidden' : '';
   }, [open]);

   return (
      <>
         <header
            className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,backdrop-filter] duration-500 ${
               scrolled ? 'border-b border-white/[0.06] bg-ink-950/70 backdrop-blur-xl' : 'border-b border-transparent'
            }`}
         >
            <div className="page flex h-16 items-center justify-between">
               <Link to="/" className="group flex items-center gap-3" aria-label="Red Sentinel home">
                  <span className="relative flex h-2.5 w-2.5">
                     <span className="absolute inset-0 rounded-full bg-signal animate-pulseDot" />
                     <span className="absolute -inset-1.5 rounded-full border border-signal/40" />
                  </span>
                  <span className="font-mono text-[12px] uppercase tracking-[0.32em] text-bone">
                     Red<span className="text-signal">/</span>Sentinel
                  </span>
               </Link>

               <nav className="hidden lg:block" aria-label="Sections">
                  <ul className="flex items-center gap-1 rounded-full border border-white/[0.08] bg-ink-900/50 p-1 backdrop-blur-md">
                     {PRIMARY_NAV.map(item => {
                        const active = isActivePath(item.path, pathname);
                        return (
                           <li key={item.path}>
                              <NavLink
                                 to={item.path}
                                 className={`relative block rounded-full px-4 py-1.5 text-sm transition-colors duration-300 ${
                                    active ? 'bg-bone text-ink-950' : 'text-bone-muted hover:text-bone'
                                 }`}
                              >
                                 {item.title}
                              </NavLink>
                           </li>
                        );
                     })}
                  </ul>
               </nav>

               <div className="flex items-center gap-6">
                  <ul className="hidden items-center gap-6 lg:flex">
                     {SECONDARY_NAV.map(item => (
                        <li key={item.path}>
                           <NavLink
                              to={item.path}
                              className={({ isActive }) =>
                                 `eyebrow underline-grow pb-0.5 hover:text-bone ${isActive ? 'text-bone' : ''}`
                              }
                           >
                              {item.title}
                           </NavLink>
                        </li>
                     ))}
                  </ul>

                  <button
                     type="button"
                     onClick={() => setOpen(o => !o)}
                     className="relative z-[60] flex h-10 w-10 items-center justify-center rounded-full border border-white/15 lg:hidden"
                     aria-expanded={open}
                     aria-label={open ? 'Close menu' : 'Open menu'}
                  >
                     <span
                        className={`absolute h-px w-4 bg-bone transition-transform duration-500 ${
                           open ? 'rotate-45' : '-translate-y-[3px]'
                        }`}
                     />
                     <span
                        className={`absolute h-px w-4 bg-bone transition-transform duration-500 ${
                           open ? '-rotate-45' : 'translate-y-[3px]'
                        }`}
                     />
                  </button>
               </div>
            </div>
         </header>

         <div
            ref={overlay}
            className="fixed inset-0 z-[55] hidden flex-col justify-between bg-ink-950 px-6 pb-10 pt-28 lg:hidden"
         >
            <ul className="space-y-2">
               {PRIMARY_NAV.map((item, i) => (
                  <li key={item.path} className="overflow-hidden">
                     <Link to={item.path} data-menu-item className="flex items-baseline gap-4">
                        <span className="font-mono text-xs text-signal">0{i + 1}</span>
                        <span
                           className={`font-display text-5xl ${
                              isActivePath(item.path, pathname) ? 'text-bone' : 'text-bone-muted'
                           }`}
                        >
                           {item.title}
                        </span>
                     </Link>
                  </li>
               ))}
            </ul>
            <ul className="flex gap-6">
               {SECONDARY_NAV.map(item => (
                  <li key={item.path} className="overflow-hidden">
                     <Link to={item.path} data-menu-item className="eyebrow block text-bone">
                        {item.title}
                     </Link>
                  </li>
               ))}
            </ul>
         </div>
      </>
   );
};

export default Header;
